import MenuIcon from '@mui/icons-material/Menu';
import MuiAppBar, { type AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import React, { type FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useInterval } from 'usehooks-ts';

import AppBarContent from './AppBarContent.js';
import { isEmergencyInProgressSelector } from '../../redux/selectors.js';


const leftDrawerWidth = 260;
const rightDrawerWidth = 360;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  leftOpen?: boolean;
  rightOpen?: boolean;
  emergency?: boolean;
}
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => !['open', 'leftOpen', 'rightOpen', 'emergency'].includes(prop as string),
})<AppBarProps>(({ theme, open, leftOpen, emergency, rightOpen }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    ...(leftOpen && { marginLeft: leftDrawerWidth }),
    ...(rightOpen && { marginRight: rightDrawerWidth }),
    width: `calc(100% - ${leftOpen ? leftDrawerWidth : 0}px - ${rightOpen ? rightDrawerWidth : 0}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(emergency && {
    transition: theme.transitions.create('background-color', {
      duration: 1000,
    }),
  }),
}));

interface MainAppBarProps {
  open: boolean;
  leftOpen: boolean;
  rightOpen: boolean;
  setOpen: (open: boolean) => void;
}

const MainAppBar: FC<MainAppBarProps> = ({ setOpen, leftOpen, rightOpen }) => {
  const [emergencyColoring, setEmergencyColoring] = useState<boolean>(false);
  const { pathname } = useLocation();
  const { patientId = '' } = useParams();
  const emergencyInProgress = useSelector(isEmergencyInProgressSelector(patientId));
  const open = leftOpen || rightOpen;
  const onPatientsProfilePage = pathname.includes(`/patients/${patientId}`);
  useInterval(
    () => {
      setEmergencyColoring(!emergencyColoring);
    },
    emergencyInProgress ? 1100 : null,
  );
  useEffect(() => {
    if (!emergencyInProgress) {
      setEmergencyColoring(false);
    }
  }, [emergencyInProgress]);

  const theme = useTheme();

  return (
    <AppBar
      position="fixed"
      open={open}
      leftOpen={leftOpen}
      rightOpen={rightOpen}
      emergency={emergencyInProgress && onPatientsProfilePage}
      color={emergencyColoring ? 'error' : theme.palette.mode === 'light' ? 'primary' : 'default'}
      enableColorOnDark
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={() => setOpen(true)}
          edge="start"
          sx={{
            marginRight: 5,
            ...(leftOpen && { display: 'none' }),
          }}
        >
          <MenuIcon />
        </IconButton>
        <AppBarContent />
      </Toolbar>
    </AppBar>
  );
};

export default MainAppBar;
